import { gql } from '@apollo/client';
import { EPersona } from '@sharedInterfaces/EPersona';
import type IWhoIAm from '@sharedInterfaces/IWhoIAm';
import { findMatchingLanguage } from '@sharedInterfaces/Language/languageHelper';
import { employeeGQL } from '@src/Objects/Employee';
import WhoIAm from '@src/Objects/WhoIAm';
import {
    setCompanyAllCertificates,
    setCompanyAllCompetences,
    setCompanyAllEmployees,
    setCompanyAllRoles,
    setCompanyAllSkills,
    setCompanyId,
    setCompanyName,
    setCompanyOpportunitySettings,
    setCompanySkillCategories,
    setCompanyStyle,
    setCompanyVersions,
} from '@store/reducer/companyReducer';
import { setEmployeeLang, setEmployeeStore } from '@store/reducer/employeeReducer';
import { setEmployeeSettings, setLanguage } from '@store/reducer/employeeSettingsReducer';
import { setLevelDefinition } from '@store/reducer/levelDefinitionReducer';
import { setAdminNotifications, setEmployeeNotifications } from '@store/reducer/notificationsReducer';
import { setOUs } from '@store/reducer/OUsReducer';
import { setPermissions } from '@store/reducer/permissionsReducer';
import { setPersonas } from '@store/reducer/personasReducer';
import { setUser } from '@store/reducer/userReducer';
import store from '@store/store';
import { entitiesGql } from '../Company/getCompanySettings';
import GraphQL from '../graphQL';
import { employeeCoverageGql } from '../Opportunity/getOpportunity';

import { emailSubscriptions } from './setEmployeeEmailSettings';

const q = gql`
query whoIAm($smallSkill: Int!, $smallCompetence: Int!, $smallCertificate: Int!, $smallEmployee: Int!, $smallRole: Int!) {
    whoIAm(smallSkill: $smallSkill, smallCompetence: $smallCompetence, smallCertificate: $smallCertificate, smallEmployee: $smallEmployee, smallRole: $smallRole) {
        user {
            employeeId,
            companyId,
            confirmed,
            email,
        },
        employee ${employeeGQL},
        smallCompetences {
            id
            title
            levels {
                id
                title
                skills {
                    id
                    title
                    level
                }
                certificates {
                    id
                    title
                }
            }         
        }
        smallSkills {
            title,
            id,
            categoryId,
            otherNames,
        },
        smallCertificates {
            id,
            title,
            durationInDays,
            otherNames,
        },
        smallEmployees {
            id,
            title,
            email,
            pictureId,
            availability{
                availability
                workDayCount
            }
        }
        smallRoles{
            id
            title
            otherNames
        }
        skillCategories {
            id,
            title,
            description,
            parentCategory,
        },
        companyTitle,
        style {
            primary,
            onPrimary,
            secondary,
            onSecondary,
            logoIdLight,
            logoIdDark,
        },
        OUs {
            id,
            parentOU,
            title,
            leader,
            employees,
        },
        level {
            level,
            title,
            description,
            helperText,
        },
        employeeSettings{
            hiddenProjects,
            firstExperience,
            competenceSettings{
                hiddenCompetences
                wantedCompetences{
                    id,
                    level,
                    untilDate,
                    createdAt,
                    state, 
                    initialCompareState
                    ${employeeCoverageGql}
                }
            }
            startPage
            lastForecastDate
            lastSkillUpdate
            goals {
                id
                title
                description
                skills{
                    id
                    title
                    level
                }
                certificates{
                    id
                    title
                }
                untilDate
                createdAt
                state
                initialCompareState
                ${employeeCoverageGql}
            }
            emailSettings{
                subscribeAll
                subscribeAllNotifications
                subscriptions
                ${emailSubscriptions}
                notificationSubscriptions
                ${emailSubscriptions}
            }
        }
        permissions{
            admin,
            ${entitiesGql}
        }
        personas
        unknownSkillCount
        language
        opportunitySettings{
            statusOptions{
                id
                title
                isActive
            }
        }
        versions{
            smallCompetence
            smallSkill
            smallCertificate
            smallEmployee 
            smallRole
        }
        notifications {
            id
            notificationType
            title
            message
            messageHTML
            version
            publishedAt
        }
        adminNotifications {
            id
            notificationType
            title
            message
            messageHTML
            version
            publishedAt
        }
    },
},
`;

export default async function getWhoIAm(callback: (MySelf: WhoIAm | null) => void) {
    const versions = store.getState().company.versions;
    const variables = {
        smallSkill: versions.smallSkill ? versions.smallSkill : 0,
        smallCompetence: versions.smallCompetence ? versions.smallCompetence : 0,
        smallCertificate: versions.smallCertificate ? versions.smallCertificate : 0,
        smallEmployee: versions.smallEmployee ? versions.smallEmployee : 0,
        smallRole: versions.smallRole ? versions.smallRole : 0,
    };
    return GraphQL.query<{ whoIAm: IWhoIAm }>(q, variables, (obj) => {
        if (obj?.whoIAm) {
            const whoIAm = new WhoIAm(obj.whoIAm);
            const state = store.getState();

            store.dispatch(
                setEmployeeStore({
                    ...whoIAm.employee,
                    language: whoIAm.language ? whoIAm.language : findMatchingLanguage(navigator.languages),
                    unknownSkillCount:
                        whoIAm.unknownSkillCount !== null ? whoIAm.unknownSkillCount : state.employee.unknownSkillCount,
                    primaryRoleId: null, // Initialize as null, will be set to first role in RolesTab
                    featuredCertificateId: null, // Initialize as null, will be set to first certificate in CertificatesTab
                }),
            );
            store.dispatch(setPermissions(whoIAm.permissions));
            store.dispatch(setUser(whoIAm.user));
            store.dispatch(setLevelDefinition(whoIAm.level));
            store.dispatch(setCompanyId(whoIAm.user.companyId));
            store.dispatch(setCompanyName(whoIAm.companyTitle));
            store.dispatch(setCompanySkillCategories(whoIAm.skillCategories));
            store.dispatch(setCompanyStyle(whoIAm.style));
            store.dispatch(setOUs(whoIAm.OUs));
            store.dispatch(setEmployeeSettings(whoIAm.employeeSettings));
            store.dispatch(setCompanyVersions(whoIAm.versions));
            store.dispatch(setCompanyOpportunitySettings(whoIAm.opportunitySettings));
            store.dispatch(setPersonas(whoIAm.personas || [EPersona.Employee]));

            store.dispatch(setLanguage(whoIAm.language));
            if (whoIAm.language) {
                store.dispatch(setEmployeeLang(whoIAm.language));
            }

            if (whoIAm.smallCompetences) store.dispatch(setCompanyAllCompetences(whoIAm.smallCompetences));
            if (whoIAm.smallSkills) store.dispatch(setCompanyAllSkills(whoIAm.smallSkills));
            if (whoIAm.smallCertificates) store.dispatch(setCompanyAllCertificates(whoIAm.smallCertificates));
            if (whoIAm.smallEmployees) store.dispatch(setCompanyAllEmployees(whoIAm.smallEmployees));
            if (whoIAm.smallRoles) store.dispatch(setCompanyAllRoles(whoIAm.smallRoles));

            // Dispatch notifications
            if (whoIAm.notifications) store.dispatch(setEmployeeNotifications(whoIAm.notifications));
            if (whoIAm.adminNotifications) store.dispatch(setAdminNotifications(whoIAm.adminNotifications));

            return callback(whoIAm);
        }
        callback(null);
    });
}
